import React, { useState, useEffect } from "react";
import { Link } from "@/lib/router-compat";
import { trackCall, trackEmail, trackQuote } from "@/lib/track";
import { scrollToId } from "@/lib/scrollToId";
import {
  Menu,
  X,
  ArrowRight,
  ChevronDown,
  Phone,
  Mail,
  MapPin,
  Facebook,
} from "lucide-react";

import {
  CEMFLOOR_LOGO_RESTORED,
  LIGHT_LOGO_RESTORED,
  DARK_LOGO_RESTORED,
} from "@/data/restoredAssets";

export const LIGHT_LOGO = LIGHT_LOGO_RESTORED;
export const DARK_LOGO =
  "https://assets.cdn.filesafe.space/iehMW4z3GZgoPjqmxz5p/media/6a7469d1e76789340f9e87b3.webp";
export const CEMFLOOR_LOGO = CEMFLOOR_LOGO_RESTORED;
export const HERO_BG =
  "https://assets.cdn.filesafe.space/iehMW4z3GZgoPjqmxz5p/media/6a85f76b5113a1715f109275.webp";
export const HEATING_IMG =
  "https://assets.cdn.filesafe.space/iehMW4z3GZgoPjqmxz5p/media/6a85f76b8bea83db8d3f8a9c.webp";

const PHONE_DISPLAY = "07765 712609";
const PHONE_HREF = "tel:077657126609";
const PHONE2_DISPLAY = "07470 861869";
const PHONE2_HREF = "tel:07470861869";
const EMAIL = "efscreed@gmail.com";
const LOCATION = "Exeter & Devon";

const services = [
  {
    name: "Underfloor Heating Screed",
    href: "/services/underfloor-heating-screed",
    desc: "Screed for underfloor heating",
  },
  {
    name: "Heavy-Duty Screed",
    href: "/services/heavy-duty-screed",
    desc: "Strong screed for garages and busy floors",
  },
  {
    name: "Ready-To-Tile Screed",
    href: "/services/ready-to-tile-screed",
    desc: "No sanding needed before tiling",
  },
  {
    name: "Rapid-Drying Screed",
    href: "/services/fast-drying-screed",
    desc: "Dries fast for tight deadlines",
  },
];

const navLinks = [
  { label: "Home", href: "/", isLink: true },
  { label: "Our Projects", href: "/projects", isLink: true },
  { label: "Our Process", href: "/#process", isLink: false },
  { label: "Reviews", href: "/#reviews", isLink: false },
  { label: "Contact", href: "/#contact", isLink: false },
];

const SocialIcon: React.FC<{
  href: string;
  label: string;
  children: React.ReactNode;
}> = ({ href, label, children }) => (
  <a
    href={href}
    target="_blank"
    rel="noreferrer"
    aria-label={label}
    className="text-stone-300 hover:text-[#C9A84C] transition-colors"
  >
    {children}
  </a>
);

export const Navbar: React.FC = () => {
  const [scrolled, setScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [servicesOpen, setServicesOpen] = useState(false);
  const [mobileServicesOpen, setMobileServicesOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    if (mobileMenuOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [mobileMenuOpen]);

  return (
    <header className="fixed top-0 left-0 right-0 z-50">
      {/* 1. Top Utility Bar */}
      <div className="bg-[#141311] text-stone-300 border-b border-stone-800/60">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center justify-between h-8 text-xs">
          {/* Left: email + location (desktop only) */}
          <div className="hidden md:flex items-center gap-5">
            <a
              href={`mailto:${EMAIL}`}
              onClick={() => trackEmail(EMAIL, "navbar_utility")}
              className="flex items-center gap-1.5 hover:text-[#C9A84C] transition-colors"
            >
              <Mail className="w-3.5 h-3.5" />
              <span>{EMAIL}</span>
            </a>
            <span className="flex items-center gap-1.5 text-stone-400">
              <MapPin className="w-3.5 h-3.5" />
              <span>{LOCATION}</span>
            </span>
          </div>
          {/* Right: phone + divider + socials */}
          <div className="flex items-center gap-3 ml-auto">
            <a
              href={PHONE_HREF}
              onClick={() => trackCall(PHONE_DISPLAY, "navbar_utility")}
              className="flex items-center gap-1.5 font-semibold hover:text-[#C9A84C] transition-colors"
            >
              <Phone className="w-3.5 h-3.5" />
              <span>{PHONE_DISPLAY}</span>
            </a>
            <span className="w-px h-3.5 bg-stone-700" aria-hidden="true" />
            <div className="flex items-center gap-2.5">
              <SocialIcon
                href="https://www.facebook.com/profile.php?id=61578220532150"
                label="Facebook"
              >
                <Facebook className="w-3.5 h-3.5" />
              </SocialIcon>
            </div>
          </div>
        </div>
      </div>

      {/* 2. Main Navigation Bar */}
      <div
        className={`bg-white transition-all duration-300 ${
          scrolled ? "shadow-md" : "shadow-sm"
        }`}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center justify-between h-16 sm:h-20">
          {/* Logo + Business Name */}
          <Link
            to="/"
            onClick={() => {
              setMobileMenuOpen(false);
              window.scrollTo({ top: 0, behavior: "smooth" });
            }}
            className="flex items-center gap-3 shrink-0"
          >
            <img
              src="https://assets.cdn.filesafe.space/iehMW4z3GZgoPjqmxz5p/media/6ac517f8bbad531ac8eef5c3.webp"
              alt="Exeter Flow Screeds Logo"
              width={52}
              height={52}
              fetchPriority="high"
              decoding="async"
              className="h-12 w-12 sm:h-14 sm:w-14 object-contain rounded-md transition-transform duration-300 hover:scale-105"
            />
            <span className="text-stone-900 font-extrabold text-xl sm:text-2xl tracking-tight leading-none">
              Exeter Flow <span className="text-[#B38D30]">Screed</span>
            </span>
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center gap-7">
            <Link
              to="/"
              className="text-stone-700 hover:text-[#B38D30] text-sm font-semibold tracking-wide transition-colors"
            >
              Home
            </Link>

            {/* Services Dropdown */}
            <div
              className="relative"
              onMouseEnter={() => setServicesOpen(true)}
              onMouseLeave={() => setServicesOpen(false)}
            >
              <button
                className="flex items-center gap-1 text-stone-700 hover:text-[#B38D30] text-sm font-semibold tracking-wide transition-colors py-2"
                aria-expanded={servicesOpen}
              >
                Services
                <ChevronDown
                  className={`w-4 h-4 transition-transform ${servicesOpen ? "rotate-180" : ""}`}
                />
              </button>
              {servicesOpen && (
                <div className="absolute top-full left-1/2 -translate-x-1/2 pt-2">
                  <div className="bg-white rounded-xl shadow-2xl border border-stone-200 p-3 w-[460px] grid grid-cols-2 gap-1.5">
                    {services.map((s) => (
                      <Link
                        key={s.name}
                        to={s.href}
                        className="block p-3 rounded-lg hover:bg-stone-50 transition-colors group"
                      >
                        <span className="block text-sm font-bold text-stone-800 group-hover:text-[#B38D30] transition-colors">
                          {s.name}
                        </span>
                        <span className="block text-xs text-stone-500 mt-0.5">
                          {s.desc}
                        </span>
                      </Link>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {navLinks.slice(1).map((link) =>
              link.isLink ? (
                <Link
                  key={link.label}
                  to={link.href}
                  className="text-stone-700 hover:text-[#B38D30] text-sm font-semibold tracking-wide transition-colors"
                >
                  {link.label}
                </Link>
              ) : (
                <a
                  key={link.label}
                  href={link.href}
                  onClick={(e) => {
                    if (link.href.startsWith("#")) {
                      e.preventDefault();
                      scrollToId(link.href);
                    }
                  }}
                  className="text-stone-700 hover:text-[#B38D30] text-sm font-semibold tracking-wide transition-colors"
                >
                  {link.label}
                </a>
              ),
            )}

            <a
              href="#contact"
              onClick={(e) => {
                e.preventDefault();
                trackQuote("navbar_desktop");
                scrollToId("#contact");
              }}
              className="inline-flex items-center gap-2 bg-[#C9A84C] hover:bg-[#b89539] text-stone-950 font-bold text-sm px-5 py-2.5 rounded-md transition-all shadow-md hover:shadow-amber-500/20 hover:-translate-y-0.5"
            >
              <span>Get a Quote</span>
              <ArrowRight className="w-4 h-4" />
            </a>
          </nav>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            aria-label="Toggle Navigation"
            className="md:hidden text-stone-800 hover:text-[#B38D30] p-2 rounded-md transition-colors"
          >
            {mobileMenuOpen ? (
              <X className="w-6 h-6" />
            ) : (
              <Menu className="w-6 h-6" />
            )}
          </button>
        </div>
        {/* 3. Bottom Border Line */}
        <div className="h-1 bg-[#C9A84C]" />
      </div>

      {/* Mobile Menu Overlay */}
      {mobileMenuOpen && (
        <div className="md:hidden fixed inset-0 top-[100px] bg-[#141311] z-40 overflow-y-auto">
          <div className="px-4 pt-8 pb-12 flex flex-col items-center text-center">
            <div className="flex flex-col items-center gap-3 mb-8">
              <a
                href={PHONE_HREF}
                onClick={() => trackCall(PHONE_DISPLAY, "navbar_mobile")}
                className="flex items-center gap-2 text-white text-lg font-bold hover:text-[#C9A84C] transition-colors"
              >
                <Phone className="w-5 h-5 text-[#C9A84C]" />
                {PHONE_DISPLAY}
              </a>
              <a
                href={PHONE2_HREF}
                onClick={() => trackCall(PHONE2_DISPLAY, "navbar_mobile")}
                className="flex items-center gap-2 text-white text-lg font-bold hover:text-[#C9A84C] transition-colors"
              >
                <Phone className="w-5 h-5 text-[#C9A84C]" />
                {PHONE2_DISPLAY}
              </a>
            </div>
            <nav className="flex flex-col w-full max-w-xs gap-1">
              <Link
                to="/"
                onClick={() => setMobileMenuOpen(false)}
                className="text-stone-100 hover:text-[#C9A84C] text-lg font-semibold py-3 border-b border-stone-800 transition-colors"
              >
                Home
              </Link>
              <button
                onClick={() => setMobileServicesOpen(!mobileServicesOpen)}
                className="flex items-center justify-between w-full text-stone-100 hover:text-[#C9A84C] text-lg font-semibold py-3 border-b border-stone-800 transition-colors"
              >
                <span>Services</span>
                <ChevronDown
                  className={`w-5 h-5 transition-transform ${mobileServicesOpen ? "rotate-180" : ""}`}
                />
              </button>
              {mobileServicesOpen && (
                <div className="flex flex-col pl-4">
                  {services.map((s) => (
                    <Link
                      key={s.name}
                      to={s.href}
                      onClick={() => setMobileMenuOpen(false)}
                      className="text-stone-300 hover:text-[#C9A84C] text-base font-medium py-2.5 border-b border-stone-800/40 transition-colors"
                    >
                      {s.name}
                    </Link>
                  ))}
                </div>
              )}
              <Link
                to="/projects"
                onClick={() => setMobileMenuOpen(false)}
                className="text-stone-100 hover:text-[#C9A84C] text-lg font-semibold py-3 border-b border-stone-800 transition-colors"
              >
                Our Projects
              </Link>
              <a
                href="#process"
                onClick={(e) => {
                  e.preventDefault();
                  setMobileMenuOpen(false);
                  scrollToId("#process");
                }}
                className="text-stone-100 hover:text-[#C9A84C] text-lg font-semibold py-3 border-b border-stone-800 transition-colors"
              >
                Our Process
              </a>
              <a
                href="#reviews"
                onClick={(e) => {
                  e.preventDefault();
                  setMobileMenuOpen(false);
                  scrollToId("#reviews");
                }}
                className="text-stone-100 hover:text-[#C9A84C] text-lg font-semibold py-3 border-b border-stone-800 transition-colors"
              >
                Reviews
              </a>
              <a
                href="#contact"
                onClick={(e) => {
                  e.preventDefault();
                  setMobileMenuOpen(false);
                  scrollToId("#contact");
                }}
                className="text-stone-100 hover:text-[#C9A84C] text-lg font-semibold py-3 border-b border-stone-800 transition-colors"
              >
                Contact
              </a>
              <a
                href="#contact"
                onClick={(e) => {
                  e.preventDefault();
                  trackQuote("navbar_mobile");
                  setMobileMenuOpen(false);
                  scrollToId("#contact");
                }}
                className="mt-6 inline-flex items-center justify-center gap-2 bg-[#C9A84C] hover:bg-[#b89539] text-stone-950 font-bold text-base py-3.5 px-4 rounded-md w-full"
              >
                <span>Get a Quote</span>
                <ArrowRight className="w-4 h-4" />
              </a>
            </nav>
          </div>
        </div>
      )}
    </header>
  );
};
